// Methods can be imported from this file to call posthog captures.
// The goal is to have a unified and typed way to call posthog captures, to keep data consistent and clean.
import posthog from "posthog-js";
import type { PostHog } from "posthog-js";
import { ApplicationMethod, ApplicationLocation } from "./analyticsTypes";

// Method subscribe to newsletter.
export function fireSubscriptionEvent() {
	try {
		posthog.capture("user_subscribed_to_newsletter");
	} catch (error) {
		console.error("Failed to capture subscription event:", error);
	}
}

// Fetch the distinct_id of the current user.
export function fetchDistinctId(): string | undefined {
	try {
		return posthog.get_distinct_id();
	} catch (error) {
		console.error("Failed to fetch distinct_id:", error);
		return undefined;
	}
}

// When user applies.
export function fireApplicationEvent(
	job_uuid: string,
	company_uuid: string,
	application_method: ApplicationMethod,
	application_location: ApplicationLocation
) {
	try {
		posthog.capture("user_applied", {
			job_uuid,
			company_uuid,
			application_method,
			application_location
		});
	} catch (error) {
		console.error("Failed to capture application event:", error);
	}
}

interface PageEvent {
	from: {
		href: string;
	};
}

type TriggerType = "navigation" | "visibility_change";

export let previousUrl: string | undefined;

export const trackingEvents = {
	capturePageview: (posthog: PostHog, url: string): void => {
		try {
			posthog.capture("$pageview", {
				$current_url: url
			});
			previousUrl = url;
		} catch (err) {
			console.error("Failed to capture $pageview event:", err);
		}
	},

	capturePageleave: (
		posthog: PostHog,
		url: string,
		trigger: TriggerType = "navigation"
	): void => {
		try {
			posthog.capture("$pageleave", {
				$current_url: url,
				trigger
			});
		} catch (err) {
			console.error(`Failed to capture $pageleave ${trigger} event:`, err);
		}
	},

	ensurePageviewExists: (posthog: PostHog, url: string): void => {
		if (!previousUrl || previousUrl !== url) {
			trackingEvents.capturePageview(posthog, url);
		}
	},

	init: (posthog: PostHog): void => {
		document.addEventListener("astro:page-load", () => {
			trackingEvents.capturePageview(posthog, window.location.href);
		});

		document.addEventListener(
			"astro:before-preparation",
			(event: PageEvent) => {
				const fromUrl = event.from.href;
				trackingEvents.ensurePageviewExists(posthog, fromUrl);
				trackingEvents.capturePageleave(posthog, fromUrl);
			}
		);

		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "hidden") {
				const currentUrl = window.location.href;
				trackingEvents.ensurePageviewExists(posthog, currentUrl);
				trackingEvents.capturePageleave(
					posthog,
					currentUrl,
					"visibility_change"
				);
			}
		});
	}
};
